/**
 * Admin LLM Costs dashboard.
 *
 * Durable cost view — reads `cache_llm` rows via
 * `getLLMCostSummary`. Unlike the per-job cost column on the Tasks
 * page, this survives BullMQ's job expiry and answers the actual
 * "what have I spent?" question.
 *
 * Layout: range-picker chips (7/30/90/all-time) + a big total-cost
 * card, a day-by-day sparkline, and two sorted tables (per-model,
 * per-agent). Everything is computed server-side; this component is
 * just presentation + range selection.
 */
import React, {useMemo, useState} from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  pcolors,
  cardSx,
  pageSx,
  pageTitleSx,
  sectionLabelSx,
  tableHeadRowSx,
  tableHeadCellSx,
  tableBodyCellSx,
} from '../../theme/planapusTheme';
import {useGetLLMCostSummary} from '../../api/graphql_admin_costs';
import {usePageTitle} from '../../hooks/usePageTitle';
import {humanizeSpecialization} from './agentLabels';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

type Range = '7d' | '30d' | '90d' | 'all';

// Use epoch (plus a safety pad) for "all time" so the range query
// still binds to a finite window — keeps the Firestore planner happy.
const ALL_TIME_SINCE = new Date('2024-01-01T00:00:00Z');

function windowFor(range: Range): {since: Date; until: Date} {
  const until = new Date();
  if (range === 'all') return {since: ALL_TIME_SINCE, until};
  const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
  const since = new Date(until.getTime() - days * 24 * 60 * 60 * 1000);
  return {since, until};
}

export default function AdminCosts({client}: Props) {
  usePageTitle('Admin – LLM Costs');
  const [range, setRange] = useState<Range>('30d');
  const {since, until} = useMemo(() => windowFor(range), [range]);

  const {data, loading, error} = useGetLLMCostSummary(
    client,
    since.toISOString(),
    until.toISOString(),
  );
  const s = data?.getLLMCostSummary;

  return (
    <Box sx={pageSx}>
      <Box sx={{mb: 3}}>
        <Typography sx={pageTitleSx}>LLM Costs</Typography>
        <Typography sx={{fontSize: 13, color: pcolors.textSecondary, mt: 0.5}}>
          Rollup from the <code>cache_llm</code> collection — survives background-job expiry.
        </Typography>
      </Box>

      {/* Range picker */}
      <Box sx={{display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap'}}>
        {(['7d', '30d', '90d', 'all'] as Range[]).map(r => (
          <Chip
            key={r}
            label={r === 'all' ? 'All time' : `Last ${r}`}
            size="small"
            onClick={() => setRange(r)}
            sx={{
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              bgcolor: r === range ? pcolors.blue[50] : pcolors.borderLight,
              color: r === range ? pcolors.blue[700] : pcolors.textSecondary,
              border: r === range ? `1px solid ${pcolors.blue[500]}` : `1px solid transparent`,
            }}
          />
        ))}
      </Box>

      {error && (
        <Alert severity="error" sx={{mb: 2}}>
          {error.message}
        </Alert>
      )}

      {loading && !s ? (
        <Box sx={{p: 4, display: 'flex', justifyContent: 'center'}}>
          <CircularProgress size={24} />
        </Box>
      ) : s ? (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
          <Box sx={{display: 'grid', gap: 2, gridTemplateColumns: {xs: '1fr', md: '1fr 2fr'}}}>
            {/* Totals card */}
            <Paper sx={{...cardSx, p: 2.5, display: 'flex', flexDirection: 'column', gap: 0.75}}>
              <Typography sx={sectionLabelSx}>Total spend</Typography>
              <Typography
                sx={{
                  fontSize: 36,
                  fontWeight: 900,
                  color: pcolors.green[700],
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1.1,
                }}
              >
                ${s.totalCostUSD.toFixed(4)}
              </Typography>
              <Typography sx={{fontSize: 12, color: pcolors.textSecondary}}>
                {s.callCount.toLocaleString()} LLM call{s.callCount === 1 ? '' : 's'} ·{' '}
                {s.totalTokens.toLocaleString()} tokens
              </Typography>
              <Typography sx={{fontSize: 10, color: pcolors.textMuted, mt: 0.5}}>
                {new Date(s.since).toLocaleDateString()} → {new Date(s.until).toLocaleDateString()}
              </Typography>
            </Paper>

            {/* Sparkline */}
            <Paper sx={{...cardSx, p: 2}}>
              <Typography sx={sectionLabelSx}>Daily cost</Typography>
              <DailySparkline daily={s.daily} />
            </Paper>
          </Box>

          {/* Per-model breakdown */}
          <Paper sx={{...cardSx, p: 0}}>
            <Box sx={{p: 1.5, pb: 0.5}}>
              <Typography sx={sectionLabelSx}>By model</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={tableHeadRowSx}>
                    <TableCell sx={tableHeadCellSx}>Model</TableCell>
                    <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Cost</TableCell>
                    <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Calls</TableCell>
                    <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Tokens</TableCell>
                    <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Share</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {s.byModel.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{fontSize: 12, color: pcolors.textMuted, py: 2, textAlign: 'center'}}>
                        No data for this window.
                      </TableCell>
                    </TableRow>
                  ) : (
                    s.byModel.map(m => (
                      <TableRow key={m.model} hover>
                        <TableCell sx={{...tableBodyCellSx, fontFamily: 'monospace', fontSize: 12}}>
                          {m.model}
                        </TableCell>
                        <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: pcolors.green[700]}}>
                          ${m.costUSD.toFixed(4)}
                        </TableCell>
                        <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                          {m.callCount.toLocaleString()}
                        </TableCell>
                        <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: pcolors.textSecondary}}>
                          {m.tokens.toLocaleString()}
                        </TableCell>
                        <TableCell sx={{...tableBodyCellSx, textAlign: 'right', color: pcolors.textMuted}}>
                          {s.totalCostUSD > 0
                            ? `${((m.costUSD / s.totalCostUSD) * 100).toFixed(1)}%`
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Per-agent breakdown */}
          <Paper sx={{...cardSx, p: 0}}>
            <Box sx={{p: 1.5, pb: 0.5}}>
              <Typography sx={sectionLabelSx}>By agent</Typography>
              <Typography sx={{fontSize: 11, color: pcolors.textMuted, mt: 0.25}}>
                Rows without an agent_id (summaries, selector, eval) land under "(none)". A
                single agent can produce multiple rows when the triage override downgrades
                ANALYSIS specialists to Flash-Lite for benign content.
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={tableHeadRowSx}>
                    <TableCell sx={tableHeadCellSx}>Agent</TableCell>
                    <TableCell sx={{...tableHeadCellSx, fontFamily: 'monospace'}}>Version</TableCell>
                    <TableCell sx={{...tableHeadCellSx, fontFamily: 'monospace'}}>Model</TableCell>
                    <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Cost</TableCell>
                    <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Calls</TableCell>
                    <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Tokens</TableCell>
                    <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Share</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {s.byAgent.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{fontSize: 12, color: pcolors.textMuted, py: 2, textAlign: 'center'}}>
                        No data for this window.
                      </TableCell>
                    </TableRow>
                  ) : (
                    s.byAgent.map(a => (
                      <TableRow key={`${a.specialization}-${a.version}-${a.model}`} hover>
                        <TableCell sx={tableBodyCellSx}>
                          {a.specialization === '(none)'
                            ? '(none)'
                            : humanizeSpecialization(a.specialization)}
                        </TableCell>
                        <TableCell sx={{...tableBodyCellSx, fontFamily: 'monospace', fontSize: 11, color: pcolors.textSecondary}}>
                          {a.version}
                        </TableCell>
                        <TableCell sx={{...tableBodyCellSx, fontFamily: 'monospace', fontSize: 11, color: pcolors.textSecondary}}>
                          {a.model}
                        </TableCell>
                        <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: pcolors.green[700]}}>
                          ${a.costUSD.toFixed(4)}
                        </TableCell>
                        <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                          {a.callCount.toLocaleString()}
                        </TableCell>
                        <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: pcolors.textSecondary}}>
                          {a.tokens.toLocaleString()}
                        </TableCell>
                        <TableCell sx={{...tableBodyCellSx, textAlign: 'right', color: pcolors.textMuted}}>
                          {s.totalCostUSD > 0
                            ? `${((a.costUSD / s.totalCostUSD) * 100).toFixed(1)}%`
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Typography sx={{fontSize: 10, color: pcolors.textMuted, textAlign: 'right'}}>
            Generated {new Date(s.generatedAt).toLocaleTimeString()}
          </Typography>
        </Box>
      ) : null}

    </Box>
  );
}

/**
 * Simple bar sparkline over daily buckets. Drawn in SVG so it scales
 * to any container width — each day gets an equal-width column,
 * height is proportional to `costUSD`. Missing days are skipped (no
 * bar) rather than filled with zero; the gaps are more informative.
 */
function DailySparkline({daily}: {daily: Array<{date: string; costUSD: number; callCount: number}>}) {
  if (daily.length === 0) {
    return (
      <Typography sx={{fontSize: 12, color: pcolors.textMuted, py: 3, textAlign: 'center'}}>
        No activity in this window.
      </Typography>
    );
  }

  const width = 600;
  const height = 120;
  const maxCost = Math.max(...daily.map(d => d.costUSD), 0.0001);
  const barWidth = daily.length > 0 ? width / daily.length : 0;
  const barPad = Math.min(2, barWidth * 0.15);

  return (
    <Box sx={{mt: 1}}>
      <Box sx={{overflow: 'hidden'}}>
        <Box
          component="svg"
          viewBox={`0 0 ${width} ${height + 24}`}
          preserveAspectRatio="none"
          sx={{width: '100%', height: 140, display: 'block'}}
        >
          {daily.map((d, i) => {
            const h = Math.max(2, (d.costUSD / maxCost) * height);
            const x = i * barWidth + barPad / 2;
            const y = height - h;
            return (
              <rect
                key={d.date}
                x={x}
                y={y}
                width={Math.max(1, barWidth - barPad)}
                height={h}
                rx={1}
                fill={pcolors.green[500]}
                opacity={0.85}
              >
                <title>
                  {d.date} · ${d.costUSD.toFixed(4)} · {d.callCount} call{d.callCount === 1 ? '' : 's'}
                </title>
              </rect>
            );
          })}
          <line
            x1={0}
            y1={height}
            x2={width}
            y2={height}
            stroke={pcolors.borderLight}
            strokeWidth={1}
          />
        </Box>
      </Box>
      <Box sx={{display: 'flex', justifyContent: 'space-between', mt: 0.5}}>
        <Typography sx={{fontSize: 10, color: pcolors.textMuted}}>
          {daily[0].date}
        </Typography>
        <Typography sx={{fontSize: 10, color: pcolors.textMuted}}>
          peak ${maxCost.toFixed(4)}
        </Typography>
        <Typography sx={{fontSize: 10, color: pcolors.textMuted}}>
          {daily[daily.length - 1].date}
        </Typography>
      </Box>
    </Box>
  );
}

